import { Grid, useMediaQuery } from "@mui/material";
import { Create, Delete } from "@mui/icons-material";
import { useAccount } from "wagmi";

import { Breadcrumbs, PageHeader } from "@dzambalaorg/mui-page-layout";
import { DeleteDialog } from "@dzambalaorg/mui-dialog-delete";
import { ListAction, ListActions, ListItem, StyledPagination } from "@framework/styled";
import type { IContract } from "@framework/types";
import { ContractStatus, LootContractFeatures } from "@framework/types";
import { useDialogDelete, useDialogForm, useFetchQuery } from "@dzambalaorg/react-hooks";
import { IPaginationResult } from "@dzambalaorg/types-collection";
import {
  BlacklistButton,
  ChainLinkSetSubscriptionButton,
  ContractAllowanceButton,
  ContractSearchForm,
  EthListenerAddButton,
  EthListenerRemoveButton,
  GrantRoleButton,
  LootContractDeployButton,
  LootContractMintButton,
  PauseButton,
  RenounceRoleButton,
  RevokeRoleButton,
  RoyaltyButton,
  ServiceType,
  SetBaseTokenURIButton,
  TopUpButton,
  TransferButton,
  UnBlacklistButton,
  UnPauseButton,
  UnWhitelistButton,
  WhitelistButton,
  WithCheckPermissionsListWrapper,
} from "@framework/shared";
import { List } from "@dzambalaorg/ui-kit";

import { LootContractDialogForm } from "./form";

const LOOT_CONTRACT_DIALOG_KEY = "lootContract";
const LOOT_CONTRACTS_URL = "/loot/contracts";

export const LootContract = () => {
  const isMd = useMediaQuery(theme => theme.breakpoints.down("md"));

  const { data, isLoading, isError, take, skip, onChangePage, hasNextPage, loadMoreRef, allRows, totalCount, refetch } =
    useFetchQuery<IPaginationResult<IContract>>(LOOT_CONTRACTS_URL, { enableInfiniteScroll: isMd }, void 0, {
      contractStatus: [ContractStatus.ACTIVE, ContractStatus.NEW],
    });

  const { handleForm, mode, selectedItem, formDialogUniqueKey } = useDialogForm<IContract>();
  const { handleDelete } = useDialogDelete<IContract>();

  const { address: account = "" } = useAccount();

  return (
    <Grid>
      <Breadcrumbs path={["dashboard", "loot", "loot.contracts"]} />

      <PageHeader message="pages.loot.contracts.title">
        <LootContractDeployButton />
      </PageHeader>

      <ContractSearchForm
        initialSearchValues={{
          contractStatus: [ContractStatus.ACTIVE, ContractStatus.NEW],
        }}
        service={ServiceType.ADMIN}
        contractFeaturesOptions={LootContractFeatures}
      />

      <WithCheckPermissionsListWrapper
        isLoading={!data && isLoading}
        isError={!data && isError}
        isEmpty={allRows.length === 0}
        count={allRows.length}
      >
        {allRows.map(contract => (
          <ListItem key={contract.id} account={account} contract={contract}>
            <List.ItemText>{contract.title}</List.ItemText>
            <ListActions dataTestId="LootActionsMenuButton">
              <ListAction
                onClick={handleForm(LOOT_CONTRACT_DIALOG_KEY, contract, "edit")}
                message="form.buttons.edit"
                dataTestId="ContractEditButton"
                icon={Create}
              />
              <ListAction
                onClick={handleDelete(contract)}
                message="form.buttons.delete"
                dataTestId="ContractDeleteButton"
                icon={Delete}
                disabled={contract.contractStatus === ContractStatus.INACTIVE}
              />
              <TopUpButton contract={contract} />
              <GrantRoleButton contract={contract} />
              <RevokeRoleButton contract={contract} />
              <RenounceRoleButton contract={contract} />
              <BlacklistButton contract={contract} />
              <UnBlacklistButton contract={contract} />
              <WhitelistButton contract={contract} />
              <UnWhitelistButton contract={contract} />
              <PauseButton contract={contract} />
              <UnPauseButton contract={contract} />
              <LootContractMintButton contract={contract} />
              <ContractAllowanceButton contract={contract} />
              <RoyaltyButton contract={contract} />
              <SetBaseTokenURIButton contract={contract} />
              <TransferButton contract={contract} />
              <ChainLinkSetSubscriptionButton contract={contract} />
              <EthListenerAddButton contract={contract} />
              <EthListenerRemoveButton contract={contract} />
            </ListActions>
          </ListItem>
        ))}
      </WithCheckPermissionsListWrapper>

      {!isMd && totalCount > take && (
        <StyledPagination
          shape="rounded"
          page={skip / take + 1}
          count={Math.ceil(totalCount / take)}
          onChange={onChangePage}
        />
      )}
      {isMd && hasNextPage && <div ref={loadMoreRef} style={{ height: "20px", margin: "20px 0" }} />}

      <DeleteDialog url={LOOT_CONTRACTS_URL} refetch={refetch} />

      <LootContractDialogForm
        open={formDialogUniqueKey === `${LOOT_CONTRACT_DIALOG_KEY}.${mode}`}
        mode={mode}
        selectedItem={selectedItem}
        url={LOOT_CONTRACTS_URL}
        refetch={refetch}
      />
    </Grid>
  );
};
